<script>
  import { showToast } from '$lib/features/app/modals';
  import { useQueryClient } from '@tanstack/svelte-query';
  import { onDestroy, untrack } from 'svelte';
  import {
    useCreateTranscriptionMutation,
    useGenerateSummarizationMutation,
    useGetTranscriptionQuery,
    useUpdateTranscriptionMutation,
  } from '../services';
  import TranscriptionConversation from './TranscriptionConversation.svelte';
  import { transcriptionStore } from './transcriptionStore';
  import { logger } from '$lib/logger';

  /**
   * @typedef {Object} Props
   * @property {any} [data]
   * @property {any} clientId
   * @property {any} calendarEventId
   * @property {boolean} [clearTranscription] - New prop to trigger clearing
   * @property {any} transcriptionStatus
   * @property {boolean} [isShowTranscription]
   * @property {() => void} [ontranscriptionCleared] - Replaces the old
   *   `on:transcriptionCleared` event; fired after a clear.
   */

  /** @type {Props} */
  let {
    data = {},
    clientId = $bindable(),
    calendarEventId = $bindable(),
    clearTranscription = false,
    transcriptionStatus = $bindable(),
    isShowTranscription = true,
    ontranscriptionCleared = undefined,
  } = $props();

  const createTranscriptionMutation = useCreateTranscriptionMutation();
  const updateTranscriptionMutation = useUpdateTranscriptionMutation();
  const generateSummarizationMutation = useGenerateSummarizationMutation();
  const transcriptionQuery = useGetTranscriptionQuery({
    clientId,
    calendarEventId,
  });
  const queryClient = useQueryClient();

  let transcriptionDraftKey = $state(clientId + '_' + calendarEventId);
  let transcriptionId = $state(null);
  let elementToScroll = $state();
  let previousText = $state('');

  onDestroy(() => {
    // Save the call data even if the call was interrupted
    if (transcriptionStatus === 'recording' && internalData.length > 0) {
      onSave();
    }
    internalData = [];
    clientId = null;
    calendarEventId = null;
    transcriptionDraftKey = null;
    transcriptionId = null;
    previousText = '';
    setTranscriptionStatus('idle');
  });

  function scrollToBottom() {
    if (elementToScroll) {
      elementToScroll.scroll({
        top: elementToScroll.scrollHeight,
        behavior: 'smooth',
      });
    }
  }

  function saveTranscriptionDraft(meetingTranscription) {
    transcriptionStore.update((currentTranscriptions) => {
      return {
        ...currentTranscriptions,
        [transcriptionDraftKey]: JSON.stringify(meetingTranscription),
      };
    });
  }

  function deleteTranscriptionDraft() {
    transcriptionStore.update((currentTranscriptions) => {
      delete currentTranscriptions[transcriptionDraftKey];
      return currentTranscriptions;
    });
  }

  function setTranscriptionStatus(status) {
    transcriptionStatus = status;
  }

  function generateSummarization(transcriptionId) {
    setTranscriptionStatus('summarizing');
    transcriptionStatus = 'summarizing';
    $generateSummarizationMutation.mutate(
      {
        transcriptionId,
      },
      {
        onSuccess: () => {
          setTranscriptionStatus('done');
          queryClient.invalidateQueries({ queryKey: ['notes', clientId] });
          showToast('Sammanfattning genererad', 'success');
        },
        onError: (e) => {
          showToast(
            'Något gick fel med att generera sammanfattningen',
            'error',
          );
          logger.error('Failed to generate summarization', {
            err: e instanceof Error ? e : undefined,
          });
        },
      },
    );
  }

  async function onSave() {
    setTranscriptionStatus('saving');
    if (transcriptionId) {
      $updateTranscriptionMutation.mutate(
        {
          transcriptionId,
          calendarEventId,
          transcription: JSON.stringify(internalData),
        },
        {
          onSuccess: () => {
            deleteTranscriptionDraft();
            generateSummarization(transcriptionId);
          },
          onError: (e) => {
            showToast('Något gick fel med att spara transkriptionen', 'error');
            logger.error('Failed to update transcription', {
              err: e instanceof Error ? e : undefined,
            });
          },
        },
      );
      return;
    } else {
      $createTranscriptionMutation.mutate(
        {
          clientId,
          calendarEventId,
          transcription: JSON.stringify(internalData),
        },
        {
          onSuccess: (data) => {
            if (data.transcriptionId) {
              deleteTranscriptionDraft();
              generateSummarization(data.transcriptionId);
            }
          },
          onError: (e) => {
            showToast('Något gick fel med att spara transkriptionen', 'error');
            logger.error('Failed to create transcription', {
              err: e instanceof Error ? e : undefined,
            });
          },
        },
      );
    }
  }
  let internalData = $derived([]);
  // Clear transcription data when clearTranscription prop becomes true
  $effect(() => {
    if (!clearTranscription) return;
    // untrack: deleteTranscriptionDraft() touches transcriptionStore, and an
    // effect tracks state read inside functions it calls.
    untrack(() => {
      internalData = [];
      previousText = '';
      deleteTranscriptionDraft();
      ontranscriptionCleared?.();
    });
  });
  // Restore the stored draft exactly ONCE. This block reads internalData.length
  // and also assigns internalData, so without the latch the assignment changes
  // what it reads and the effect retriggers forever
  // (effect_update_depth_exceeded). The legacy `$:` was safe only because its
  // dependencies were resolved at compile time.
  let hydratedFromStorage = false;
  $effect.pre(() => {
    const queryData = $transcriptionQuery.data;
    if (!queryData || transcriptionDraftKey === null || hydratedFromStorage) {
      return;
    }
    hydratedFromStorage = true;

    untrack(() => {
      // As we intercept the stream from DailyJS on the frontend, we first store
      // it in local storage. After the meeting concludes we sync to the server,
      // so an interrupted meeting can still be restored from local storage.
      const draft = $transcriptionStore?.[transcriptionDraftKey];
      if (draft != undefined) {
        internalData = JSON.parse(draft);
      }

      if (queryData.transcription) {
        transcriptionId = queryData.transcription?.id;
      }

      // Nothing in local storage — fall back to the stored transcription.
      if (!internalData.length && queryData.transcription?.transcription) {
        internalData = JSON.parse(queryData.transcription.transcription);
      }
    });
  });
  // untrack: saveTranscriptionDraft() writes transcriptionStore, which this
  // effect would otherwise pick up as a dependency.
  $effect(() => {
    if (data) {
      if (data.text && data.text !== previousText) {
        previousText = data.text;
        const lastElement = internalData[internalData.length - 1];
        if (lastElement && lastElement.participantId === data.participantId) {
          internalData[internalData.length - 1].text += ` ${data.text}`;
        } else {
          internalData = [...internalData, data];
        }

        // Store the data in local storage to prevent data loss in case of call interruption
        untrack(() => saveTranscriptionDraft(internalData));
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    }
  });
  // Autosave the transcription draft on the server when transcription is done
  // untrack: onSave() reads the create/update mutation stores to call .mutate(),
  // and an effect tracks state read inside functions it calls — mutating would
  // otherwise re-fire this and re-POST.
  $effect(() => {
    if (transcriptionStatus) {
      if (transcriptionStatus === 'finished' && internalData.length > 0) {
        untrack(() => onSave());
      }
    }
  });
  // Plain $effect: scrolls a DOM node, so it needs the node in place.
  $effect(() => {
    if (isShowTranscription && elementToScroll) {
      scrollToBottom();
    }
  });
</script>

{#if isShowTranscription}
  <div
    class="absolute bottom-0 z-20 h-full w-full max-w-[600px] bg-gray-5/10 backdrop-blur-sm"
  >
    {#if internalData.length === 0}
      <div class="p-4 italic">
        Automatisk textning är på, starta konversationen för att se framstegen
      </div>
    {:else}
      <div
        class="flex h-full flex-col space-y-1 overflow-y-auto whitespace-pre-line px-2 font-mono"
        bind:this={elementToScroll}
      >
        <div class="p-4">
          <TranscriptionConversation data={internalData} />
        </div>
      </div>
    {/if}
  </div>
{/if}
